import { useEffect, useMemo, useState } from "react";
import {
    Modal,
    Form,
    InputNumber,
    Select,
    Table,
    Tag,
    Space,
    Button,
    Popconfirm,
    message,
    Alert,
    Card,
    Typography,
} from "antd";
import {
    PlusOutlined,
    StopOutlined,
    CheckCircleOutlined,
    PercentageOutlined,
} from "@ant-design/icons";
import {
    get_billing_pricesApi,
    create_billing_priceApi,
    update_billing_priceApi,
    set_billing_price_statusApi,
    get_billing_settingsApi,
    update_yearly_discountApi,
} from "../../api/admin.billing.apis";
import { getUserRole } from "../../utils/token";

const { Option } = Select;
const { Text } = Typography;

const SUPER_ADMIN_ROLES = ["super_admin", "Super Admin", "super-admin"];

const formatDiscountPreview = (monthlyAmount, discountPct, currency = "SAR") => {
    if (monthlyAmount == null || monthlyAmount < 0) return null;
    const factor = Math.max(0, 1 - Number(discountPct) / 100);
    const annualMonthly = Math.round(monthlyAmount * factor * 100) / 100;
    const annualTotal = Math.round(monthlyAmount * 12 * factor * 100) / 100;
    return {
        annualMonthly,
        annualTotal,
        label: `${currency} ${annualMonthly.toFixed(2)}/mo · billed ${currency} ${annualTotal.toFixed(2)}/yr (Save ${Number(discountPct).toFixed(0)}%)`,
    };
};

const BillingPricesPanel = ({ plans }) => {
    const [prices, setPrices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [planFilter, setPlanFilter] = useState(undefined);
    const [createOpen, setCreateOpen] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    const [discountForm] = Form.useForm();

    const [discountPct, setDiscountPct] = useState(25);
    const [discountLoading, setDiscountLoading] = useState(false);
    const [discountSaving, setDiscountSaving] = useState(false);

    const isSuperAdmin = SUPER_ADMIN_ROLES.includes(getUserRole());
    const watchedAmount = Form.useWatch("amount", form);

    const fetchPrices = async () => {
        try {
            setLoading(true);
            const params = {};
            if (planFilter) params.plan_key = planFilter;
            const response = await get_billing_pricesApi(params);
            if (response.data.status === "success") {
                setPrices(response.data.data?.prices || []);
            } else {
                message.error(response.data.message || "Failed to load prices");
            }
        } catch (error) {
            console.error(error);
            message.error("Error loading prices");
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            setDiscountLoading(true);
            const response = await get_billing_settingsApi();
            if (response.data.status === "success") {
                const pct = Number(
                    response.data.data?.yearly_discount_percentage ?? 25
                );
                setDiscountPct(pct);
                discountForm.setFieldsValue({ percentage: pct });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setDiscountLoading(false);
        }
    };

    useEffect(() => {
        fetchPrices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planFilter]);

    useEffect(() => {
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSaveDiscount = async (values) => {
        try {
            setDiscountSaving(true);
            const response = await update_yearly_discountApi(values.percentage);
            if (response.data.status === "success") {
                const pct = Number(
                    response.data.data?.yearly_discount_percentage ?? values.percentage
                );
                setDiscountPct(pct);
                message.success(
                    response.data.message ||
                        `Yearly discount set to ${pct}%. All yearly prices recalculated.`
                );
                fetchPrices();
            } else {
                message.error(response.data.message || "Failed to update discount");
            }
        } catch (error) {
            console.error(error);
            message.error(
                error?.response?.data?.message || "Failed to update yearly discount"
            );
        } finally {
            setDiscountSaving(false);
        }
    };

    const handleCreate = async (values) => {
        try {
            setCreateLoading(true);
            // Only create monthly — backend derives yearly automatically.
            const response = await create_billing_priceApi({
                plan_key: values.plan_key,
                interval: "month",
                amount: values.amount,
                currency: values.currency || "SAR",
                status: values.status || "active",
            });
            if (response.data.status === "success") {
                message.success(
                    `Monthly price created. Yearly auto-calculated at ${discountPct}% off.`
                );
                setCreateOpen(false);
                form.resetFields();
                fetchPrices();
            } else {
                message.error(response.data.message || "Failed to create price");
            }
        } catch (error) {
            console.error(error);
            message.error(
                error?.response?.data?.message || "Failed to create price"
            );
        } finally {
            setCreateLoading(false);
        }
    };

    const handleEdit = async (values) => {
        try {
            setEditLoading(true);
            const payload = {
                currency: values.currency || "SAR",
                status: values.status,
            };
            // Yearly amount is derived — only send amount for monthly rows.
            if (selected?.interval === "month") {
                payload.amount = values.amount;
            }
            const response = await update_billing_priceApi(selected._id, payload);
            if (response.data.status === "success") {
                message.success(
                    selected?.interval === "month"
                        ? "Monthly price updated. Yearly recalculated."
                        : "Price updated"
                );
                setEditOpen(false);
                setSelected(null);
                fetchPrices();
            } else {
                message.error(response.data.message || "Failed to update price");
            }
        } catch (error) {
            console.error(error);
            message.error(
                error?.response?.data?.message || "Failed to update price"
            );
        } finally {
            setEditLoading(false);
        }
    };

    const handleSetStatus = async (row, status) => {
        try {
            const response = await set_billing_price_statusApi(row._id, status);
            if (response.data.status === "success") {
                message.success(`Price set to ${status}`);
                fetchPrices();
            } else {
                message.error(response.data.message || "Failed to update status");
            }
        } catch (error) {
            console.error(error);
            message.error("Failed to update price status");
        }
    };

    const createPreview = useMemo(
        () => formatDiscountPreview(watchedAmount, discountPct, "SAR"),
        [watchedAmount, discountPct]
    );

    const columns = [
        { title: "Plan", dataIndex: "plan_key", key: "plan_key" },
        {
            title: "Interval",
            dataIndex: "interval",
            key: "interval",
            render: (v, r) => (
                <Space>
                    <span>{v === "year" ? "Yearly" : "Monthly"}</span>
                    {v === "year" && r.derived_from_monthly ? (
                        <Tag color="cyan">Auto</Tag>
                    ) : null}
                </Space>
            ),
        },
        {
            title: "Amount",
            key: "amount",
            render: (_, r) => `${r.currency} ${Number(r.amount).toFixed(2)}`,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) =>
                status === "active" ? (
                    <Tag color="green">Active</Tag>
                ) : (
                    <Tag>Inactive</Tag>
                ),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => {
                const isActive = record.status === "active";
                const isYearly = record.interval === "year";
                return (
                    <Space>
                        <Button
                            type="link"
                            onClick={() => {
                                setSelected(record);
                                editForm.setFieldsValue({
                                    amount: record.amount,
                                    currency: record.currency,
                                    status: record.status,
                                });
                                setEditOpen(true);
                            }}
                        >
                            {isYearly ? "View" : "Edit"}
                        </Button>
                        <Popconfirm
                            title={
                                isActive
                                    ? "Deactivate this price?"
                                    : "Activate this price?"
                            }
                            onConfirm={() =>
                                handleSetStatus(record, isActive ? "inactive" : "active")
                            }
                        >
                            <Button
                                type="link"
                                danger={isActive}
                                icon={
                                    isActive ? (
                                        <StopOutlined />
                                    ) : (
                                        <CheckCircleOutlined />
                                    )
                                }
                            >
                                {isActive ? "Deactivate" : "Activate"}
                            </Button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <>
            <Card
                size="small"
                title={
                    <Space>
                        <PercentageOutlined />
                        Annual discount
                    </Space>
                }
                style={{ marginBottom: 16 }}
                loading={discountLoading}
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`Yearly = monthly × 12 × (1 − ${Number(discountPct).toFixed(0)}%)`}
                    description="Set the monthly price only. Yearly amounts and the public “Save X%” badge are calculated from this discount. Only Super Admin can change the percentage."
                />
                <Form
                    form={discountForm}
                    layout="inline"
                    onFinish={handleSaveDiscount}
                    initialValues={{ percentage: discountPct }}
                >
                    <Form.Item
                        name="percentage"
                        label="Discount %"
                        rules={[
                            { required: true, message: "Required" },
                            {
                                type: "number",
                                min: 0,
                                max: 100,
                                message: "0–100",
                            },
                        ]}
                    >
                        <InputNumber
                            min={0}
                            max={100}
                            step={1}
                            disabled={!isSuperAdmin}
                            addonAfter="%"
                            style={{ width: 140 }}
                        />
                    </Form.Item>
                    {isSuperAdmin ? (
                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={discountSaving}
                            >
                                Save & recalculate yearly
                            </Button>
                        </Form.Item>
                    ) : (
                        <Text type="secondary">
                            Ask a Super Admin to change the discount.
                        </Text>
                    )}
                </Form>
            </Card>

            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="One active price per plan + interval + currency"
                description="Add or edit the monthly amount. The yearly price is created/updated automatically from the monthly rate and the annual discount above."
            />
            <Space style={{ marginBottom: 16 }} wrap>
                <Select
                    allowClear
                    placeholder="Filter by plan"
                    style={{ width: 220 }}
                    value={planFilter}
                    onChange={setPlanFilter}
                >
                    {(plans || []).map((p) => (
                        <Option key={p.key} value={p.key}>
                            {p.key} — {p.name?.en || ""}
                        </Option>
                    ))}
                </Select>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateOpen(true)}
                >
                    Add Monthly Price
                </Button>
                <Tag color="blue">Save {Number(discountPct).toFixed(0)}% on annual</Tag>
            </Space>

            <Table
                columns={columns}
                dataSource={prices}
                loading={loading}
                rowKey="_id"
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title="Create Monthly Price"
                open={createOpen}
                onCancel={() => setCreateOpen(false)}
                onOk={() => form.submit()}
                confirmLoading={createLoading}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreate}
                    initialValues={{ currency: "SAR", status: "active" }}
                >
                    <Form.Item
                        name="plan_key"
                        label="Plan"
                        rules={[{ required: true, message: "Select a plan" }]}
                    >
                        <Select placeholder="Select plan">
                            {(plans || []).map((p) => (
                                <Option key={p.key} value={p.key}>
                                    {p.key} — {p.name?.en || ""}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="amount"
                        label="Monthly amount"
                        rules={[{ required: true }]}
                    >
                        <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                    </Form.Item>
                    {createPreview ? (
                        <Alert
                            type="success"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Yearly (auto)"
                            description={createPreview.label}
                        />
                    ) : null}
                    <Form.Item name="currency" label="Currency" initialValue="SAR">
                        <Select>
                            <Option value="SAR">SAR</Option>
                            <Option value="USD">USD</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="status" label="Status" initialValue="active">
                        <Select>
                            <Option value="active">Active</Option>
                            <Option value="inactive">Inactive</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`${selected?.interval === "year" ? "Yearly price" : "Edit monthly price"} — ${selected?.plan_key || ""}`}
                open={editOpen}
                onCancel={() => {
                    setEditOpen(false);
                    setSelected(null);
                }}
                onOk={() => {
                    if (selected?.interval === "year") {
                        setEditOpen(false);
                        setSelected(null);
                        return;
                    }
                    editForm.submit();
                }}
                confirmLoading={editLoading}
                destroyOnClose
                okText={selected?.interval === "year" ? "Close" : "Save"}
                cancelButtonProps={{
                    style:
                        selected?.interval === "year"
                            ? { display: "none" }
                            : undefined,
                }}
            >
                {selected?.interval === "year" ? (
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="Yearly amount is calculated automatically"
                        description={`Update the monthly price or the ${Number(discountPct).toFixed(0)}% annual discount to change this value.`}
                    />
                ) : null}
                <Form form={editForm} layout="vertical" onFinish={handleEdit}>
                    <Form.Item
                        name="amount"
                        label="Amount"
                        rules={[{ required: true }]}
                    >
                        <InputNumber
                            min={0}
                            step={0.01}
                            style={{ width: "100%" }}
                            disabled={selected?.interval === "year"}
                        />
                    </Form.Item>
                    <Form.Item name="currency" label="Currency">
                        <Select disabled={selected?.interval === "year"}>
                            <Option value="SAR">SAR</Option>
                            <Option value="USD">USD</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="status" label="Status">
                        <Select disabled={selected?.interval === "year"}>
                            <Option value="active">Active</Option>
                            <Option value="inactive">Inactive</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default BillingPricesPanel;
